import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { loadGoogleIdentityServices, requestGoogleAccessToken } from "@limrun/play-auth";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID as string | undefined;

export type GooglePlaySession = {
	/** True when a Google OAuth client ID is configured for this deployment. */
	configured: boolean;
	isSignedIn: boolean;
	signingIn: boolean;
	error?: string;
	/**
	 * Warm up the Google sign-in script (e.g. on page mount) so signIn stays
	 * synchronous with the click and popup blockers allow the consent popup.
	 */
	preload: () => Promise<boolean>;
	/** Opens the Google consent popup. Call from a user gesture. */
	signIn: () => Promise<string | undefined>;
	/**
	 * Current androidpublisher access token, if signed in. Read through a ref
	 * so a publish right after signIn in the same handler sees the token
	 * before React re-renders. The token is short-lived and never stored.
	 */
	getAccessToken: () => string | undefined;
};

const GooglePlaySessionContext = createContext<GooglePlaySession | undefined>(undefined);

export function GooglePlaySessionProvider({ children }: { children: ReactNode }) {
	const [accessToken, setAccessToken] = useState<string>();
	const [signingIn, setSigningIn] = useState(false);
	const [error, setError] = useState<string>();
	const accessTokenRef = useRef<string | undefined>(undefined);
	const busyRef = useRef(false);

	const preload = useCallback(
		() =>
			loadGoogleIdentityServices().then(
				() => true,
				() => false,
			),
		[],
	);

	const signIn = useCallback(async () => {
		if (busyRef.current) {
			return undefined;
		}
		if (!GOOGLE_CLIENT_ID) {
			setError("Google sign-in is not configured for this console deployment.");
			return undefined;
		}
		busyRef.current = true;
		setSigningIn(true);
		setError(undefined);
		try {
			const token = await requestGoogleAccessToken({ clientId: GOOGLE_CLIENT_ID });
			accessTokenRef.current = token;
			setAccessToken(token);
			return token;
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : "Google sign-in failed");
			return undefined;
		} finally {
			busyRef.current = false;
			setSigningIn(false);
		}
	}, []);

	const getAccessToken = useCallback(() => accessTokenRef.current, []);

	const value = useMemo<GooglePlaySession>(
		() => ({
			configured: !!GOOGLE_CLIENT_ID,
			isSignedIn: accessToken !== undefined,
			signingIn,
			error,
			preload,
			signIn,
			getAccessToken,
		}),
		[accessToken, signingIn, error, preload, signIn, getAccessToken],
	);

	return <GooglePlaySessionContext.Provider value={value}>{children}</GooglePlaySessionContext.Provider>;
}

export function useGooglePlaySession(): GooglePlaySession {
	const session = useContext(GooglePlaySessionContext);
	if (!session) {
		throw new Error("useGooglePlaySession must be used inside GooglePlaySessionProvider");
	}
	return session;
}
